/**
 * Product Setup Hooks
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import {
  createProductSetup,
  deleteProductSetup,
  getProductSetupList,
  updateProductSetup,
  changeProductStatus,
  uploadProductImageApi,
} from "../api/productsSetup";
import { queryKey } from "../constants";
import { IProductForm, ICommonQueryParams } from "../types/productSetup";

/**
 * Hook to fetch product list
 */
export const useProductList = ({ queryParams }: { queryParams?: ICommonQueryParams } = {}) => {
  return useQuery({
    queryKey: [queryKey.getProductSetupList, queryParams],
    queryFn: () => getProductSetupList(queryParams),
  });
};

/**
 * Hook to create a new product
 */
export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: IProductForm) => createProductSetup(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getProductSetupList] });
      toast.success("Product created successfully");
    },
  });
};

/**
 * Hook to update an existing product
 */
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<IProductForm> }) =>
      updateProductSetup({ id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getProductSetupList] });
      toast.success("Product updated successfully");
    },
  });
};

/**
 * Hook to delete a product
 */
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteProductSetup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getProductSetupList] });
      toast.success("Product deleted successfully");
    },
  });
};

/**
 * Hook to change product status
 */
export const useChangeProductStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { statuscode: number } }) =>
      changeProductStatus({ id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getProductSetupList] });
      toast.success("Product status updated successfully");
    },
  });
};

/**
 * Hook to upload product image
 */
export const useUploadProductImage = () => {
  return useMutation({
    mutationFn: ({
      imageData,
      productGuid,
      fileName,
      contentType
    }: {
      imageData: File | Blob | ArrayBuffer;
      productGuid: string;
      fileName: string;
      contentType?: string;
    }) => uploadProductImageApi(imageData, productGuid, fileName, contentType),
    onSuccess: () => {
      toast.success("Product image uploaded successfully");
    },
  });
};
